/* ==========================================================================
   Executive Beverage — Numeric limit guard
   --------------------------------------------------------------------------
   ONE enforcement layer for every min/max business rule on the site: the
   eight-guest cocktail class minimum, the ten-guest Crush Kit minimum, the
   twenty-seat daily class capacity, the minimum hours of bar service, and
   "guests under 21 cannot exceed the total guest count".

   Every one of those rules used to be a sentence of hint text and nothing
   more. Nothing stopped a six-guest class enquiry, nothing stopped fifty
   under-21s inside a party of twenty, and nothing stopped a twenty-guest
   class being requested on a day already holding twenty seats.

   WHY NOT NATIVE min/max? The numeric fields are deliberately
   type="text" inputmode="numeric", not type="number" — a legacy theme script
   rewrites input[type=number] into its own quantity-selector markup and
   strips the data-eb-* attributes the estimate maths depends on. See the
   comment at the top of sections/eb-estimate.liquid. So the constraint lives
   in data attributes and is enforced here.

   MARKUP CONTRACT, on the input itself:
     data-eb-min="8"                  floor
     data-eb-max="20"                 ceiling
     data-eb-max-from="#eb-guests"    ceiling read live from another field
     data-eb-min-from="#other"        floor read live from another field
     data-eb-unit="guests"            word used in the generated message
     data-eb-min-msg="..."            override the message; {min} substitutes
     data-eb-max-msg="..."            override the message; {max} substitutes
     data-eb-min-date-hours="24"      date floor = now + N hours (0 = today)

   Bounds may be rewritten at runtime: eb-class.js republishes data-eb-max on
   the guest field as the seats still free on the chosen date, then calls
   EBLimits.refresh().

   HOW IT BLOCKS:
     - setCustomValidity(), so the estimate wizard's existing checkValidity()
       step gate rejects the step with no change to eb-estimate.js at all
     - a submit listener on the owning form, so single-step forms stop too
     - data-eb-invalid on the wrapping field, reusing the red styling that
       already exists in snippets/eb-form-styles.liquid

   WHEN IT SPEAKS: a field is not scolded while it is first being typed into.
   Errors appear on blur or on a submit attempt; after that the message
   updates live on every keystroke, so the correction is immediate.

   PROGRESSIVE ENHANCEMENT, deliberately: if this file fails to load, the
   forms still submit. A wrong guest count that reaches the inbox is a lead
   somebody can correct in a reply. A form that cannot submit is a lost
   booking.

   Portable: plain ES5-compatible DOM code, no Shopify specifics.
   ========================================================================== */
(function () {
  'use strict';

  var SEL =
    '[data-eb-min],[data-eb-max],[data-eb-max-from],[data-eb-min-from],[data-eb-min-date-hours]';

  /* ---------------------------------------------------------------- helpers */

  function toNum(v) {
    if (v === null || v === undefined) return NaN;
    var s = String(v).replace(/[^0-9.\-]/g, '');
    if (s === '' || s === '-' || s === '.') return NaN;
    return parseFloat(s);
  }

  // A bound is either a literal on the element or a live read of another
  // field. A blank source field means "no bound yet", not "bound of zero" —
  // returning 0 there would reject every entry until the source is filled.
  function bound(el, litAttr, fromAttr) {
    var from = el.getAttribute(fromAttr);
    if (from) {
      var src = document.querySelector(from);
      if (!src) return NaN;
      return toNum(src.value);
    }
    return toNum(el.getAttribute(litAttr));
  }

  function wrapOf(el) {
    if (!el.closest) return el.parentElement;
    return (
      el.closest('[data-eb-field]') ||
      el.closest('.eb-est__field') ||
      el.closest('.eb-contact__field') ||
      el.closest('.eb-class__field') ||
      el.parentElement
    );
  }

  // The message node is created on demand rather than authored into every
  // form, so adding a limit to a new field is a one-attribute change.
  function msgNode(el) {
    var wrap = wrapOf(el);
    if (!wrap) return null;
    var node = wrap.querySelector('[data-eb-limit-msg]');
    if (!node) {
      node = document.createElement('span');
      node.className = 'eb-limit-msg';
      node.setAttribute('data-eb-limit-msg', '');
      node.setAttribute('role', 'alert');
      node.hidden = true;
      wrap.appendChild(node);
    }
    return node;
  }

  function unitOf(el) {
    var u = el.getAttribute('data-eb-unit');
    return u ? ' ' + u : '';
  }

  function fill(tpl, n) {
    return String(tpl).replace(/\{(min|max)\}/g, String(n));
  }

  /* ------------------------------------------------------------ date floors */

  // Computed at runtime, never in Liquid: a Liquid-rendered "today" would be
  // frozen into Shopify's page cache and could reject a perfectly valid date
  // hours later.
  function applyDateFloor(el) {
    var hours = parseFloat(el.getAttribute('data-eb-min-date-hours'));
    if (!isFinite(hours)) return;
    var t = new Date(Date.now() + hours * 3600 * 1000);
    var iso =
      t.getFullYear() +
      '-' +
      ('0' + (t.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + t.getDate()).slice(-2);
    el.min = iso;
  }

  /* ------------------------------------------------------------- validation */

  function check(el, show) {
    var raw = String(el.value == null ? '' : el.value).trim();
    var problem = '';

    // An empty field is the `required` attribute's business, not ours.
    // Bounds only describe what a value must look like once there is one.
    if (raw !== '' && !el.disabled && el.type !== 'date') {
      var val = toNum(raw);
      var min = bound(el, 'data-eb-min', 'data-eb-min-from');
      var max = bound(el, 'data-eb-max', 'data-eb-max-from');

      var maxMsg = function () {
        return el.getAttribute('data-eb-max-msg')
          ? fill(el.getAttribute('data-eb-max-msg'), max)
          : 'The most we can take is ' + max + unitOf(el) + '.';
      };

      /* The ceiling can drop below the floor — a class day with six seats
         left cannot host an eight-guest minimum. There is no legal value at
         all then, so the floor message ("enter at least 8") would send the
         visitor chasing a number that is also rejected. Report the ceiling
         instead: it is the constraint that actually moved, and picking a
         different date is the action that resolves it. */
      var impossible = isFinite(min) && isFinite(max) && max < min;

      if (!isFinite(val)) {
        problem = 'Please enter a number.';
      } else if (impossible) {
        problem = maxMsg();
      } else if (isFinite(min) && val < min) {
        problem = el.getAttribute('data-eb-min-msg')
          ? fill(el.getAttribute('data-eb-min-msg'), min)
          : 'Enter at least ' + min + unitOf(el) + '.';
      } else if (isFinite(max) && val > max) {
        problem = maxMsg();
      }
    }

    if (typeof el.setCustomValidity === 'function') el.setCustomValidity(problem);

    var wrap = wrapOf(el);
    var node = msgNode(el);
    if (problem && show) {
      if (wrap) wrap.setAttribute('data-eb-invalid', '');
      if (node) {
        node.textContent = problem;
        node.hidden = false;
      }
    } else if (!problem) {
      if (wrap) wrap.removeAttribute('data-eb-invalid');
      if (node) {
        node.textContent = '';
        node.hidden = true;
      }
    }
    return !problem;
  }

  function fieldsIn(scope) {
    var out = [];
    var nodes = scope.querySelectorAll(SEL);
    for (var i = 0; i < nodes.length; i++) out.push(nodes[i]);
    return out;
  }

  /* ------------------------------------------------------------------- wire */

  function bindField(el) {
    if (el.__ebLimit) return;
    el.__ebLimit = true;

    applyDateFloor(el);

    el.addEventListener('blur', function () {
      el.__ebTouched = true;
      check(el, true);
    });
    el.addEventListener('input', function () {
      check(el, !!el.__ebTouched);
      // A field whose ceiling comes from this one has to be re-judged too:
      // raising the total guest count can make an under-21 count legal again.
      dependants(el);
    });
    el.addEventListener('change', function () {
      check(el, !!el.__ebTouched);
      dependants(el);
    });

    check(el, false);
  }

  // Anything reading its bound from `el` via a selector that matches `el`.
  function dependants(el) {
    if (!el.id) return;
    var all = fieldsIn(document);
    for (var i = 0; i < all.length; i++) {
      var other = all[i];
      if (other === el) continue;
      var from =
        other.getAttribute('data-eb-max-from') || other.getAttribute('data-eb-min-from');
      if (from && document.querySelector(from) === el) {
        check(other, !!other.__ebTouched);
      }
    }
  }

  /* ONE listener, on document, in the CAPTURE phase.

     Capture on document fires before every target and bubble listener on the
     page, whoever registered first. That matters: the section scripts are
     parsed before this one binds, and a third-party script listens on
     document too. A per-form listener would sit somewhere unpredictable in
     that order; this one is always first, so nothing else can act on a form
     we are about to reject. */
  function guard(event) {
    var form = event.target;
    if (!form || form.nodeName !== 'FORM') return;
    var fields = fieldsIn(form);
    if (!fields.length) return;

    var bad = null;
    for (var i = 0; i < fields.length; i++) {
      fields[i].__ebTouched = true;
      if (!check(fields[i], true) && !bad) bad = fields[i];
    }
    if (bad) {
      event.preventDefault();
      /* preventDefault alone is NOT enough here. A document-level submit
         handler also listens on these contact forms and posts them over
         AJAX; because preventDefault does not stop propagation, that handler
         would still fire and send an invalid enquiry. Verified in the
         browser: the event reached `document` with defaultPrevented already
         true. So an invalid submit is stopped dead instead. On a valid
         submit the event is left completely untouched, and the normal path —
         including that AJAX handler — runs exactly as before. */
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      if (bad.scrollIntoView) {
        bad.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      bad.focus();
    }
  }

  function scan(scope) {
    var fields = fieldsIn(scope || document);
    for (var i = 0; i < fields.length; i++) bindField(fields[i]);
    return fields;
  }

  /* -------------------------------------------------------------- public API */

  window.EBLimits = {
    // Re-judge everything. Called by eb-class.js after it rewrites the guest
    // ceiling to match the seats still free on the chosen date.
    refresh: function (scope) {
      var fields = fieldsIn(scope || document);
      for (var i = 0; i < fields.length; i++) check(fields[i], !!fields[i].__ebTouched);
    },
    check: function (el) {
      return check(el, !!el.__ebTouched);
    },
    scan: scan
  };

  function boot() {
    scan(document);
    document.addEventListener('submit', guard, true);
    // Theme editor: sections re-render without a page reload.
    document.addEventListener('shopify:section:load', function (event) {
      scan(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
